import { ComputedRef, Ref } from 'vue';
import type {
  GridRawRowDataType,
  GridTableInstance,
  GridCellInstance,
  GridHeaderInstance,
  GridColumnPinningOption,
} from '../../modernGrid.types';

export const useColumnPinning = <RowDataType extends GridRawRowDataType>({
  table,
  columnPinningInfo,
}: {
  table: Ref<GridTableInstance<RowDataType> | undefined>;
  columnPinningInfo: ComputedRef<GridColumnPinningOption>;
}) => {
  const getLeftPosition = (
    pinnedIndex: number,
    cells: (GridCellInstance<RowDataType> | GridHeaderInstance<RowDataType>)[],
  ): number =>
    cells?.reduce((position, cell) => {
      if (cell.column.getIsPinned() === 'left' && cell.column.getPinnedIndex() < pinnedIndex) {
        return position + cell.column.getSize();
      }
      return position;
    }, 0);

  const getRightPosition = (
    pinnedIndex: number,
    cells: (GridCellInstance<RowDataType> | GridHeaderInstance<RowDataType>)[],
  ): number =>
    cells?.reduce((position, cell) => {
      if (cell.column.getIsPinned() === 'right' && cell.column.getPinnedIndex() > pinnedIndex) {
        return position + cell.column.getSize();
      }
      return position;
    }, 0);

  const getPinnedCellStyle = (
    cell: GridCellInstance<RowDataType> | GridHeaderInstance<RowDataType>,
    cells: (GridCellInstance<RowDataType> | GridHeaderInstance<RowDataType>)[],
  ): {
    left?: string;
    right?: string;
  } | null => {
    const pinnedSide = cell.column.getIsPinned();
    const pinnedIndex = cell.column.getPinnedIndex();

    if (!pinnedSide || pinnedIndex === -1) {
      return null;
    }

    if (pinnedSide === 'left' && !!columnPinningInfo.value.useLeftSticky) {
      const leftPosition = getLeftPosition(pinnedIndex, cells);
      return {
        left: `${leftPosition}px`,
      };
    }

    if (pinnedSide === 'right' && !!columnPinningInfo.value.useRightSticky) {
      const rightPosition = getRightPosition(pinnedIndex, cells);
      return {
        right: `${rightPosition}px`,
      };
    }

    return null;
  };

  const getPinnedClasses = (
    cell: GridCellInstance<RowDataType> | GridHeaderInstance<RowDataType>,
  ): Record<string, boolean> => {
    const { column } = cell;
    const isPinned = column.getIsPinned();
    const { useLeftSticky, useRightSticky } = columnPinningInfo.value || {};
    const leftPinnedCount = table.value?.getState().columnPinning?.left?.length || 0;

    return {
      'is-pinned-left': isPinned === 'left',
      'is-pinned-right': isPinned === 'right',
      'is-sticky-column-left': !!useLeftSticky && isPinned === 'left',
      'is-sticky-column-right': !!useRightSticky && isPinned === 'right',
      'edge-sticky-left':
        !!useLeftSticky && isPinned === 'left' && column.getPinnedIndex() === leftPinnedCount - 1,
      'edge-sticky-right':
        !!useRightSticky && isPinned === 'right' && column.getPinnedIndex() === 0,
    };
  };

  return {
    getPinnedCellStyle,
    getPinnedClasses,
  };
};
